import { createContext, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { onIdTokenChanged } from "firebase/auth";
import { auth } from "../firebases";
import { getRoleLabelByLevel, normalizeRoleLevel } from "utils/roleUtils";
import getGraphQLEndpoint from "utils/getGraphQLEndpoint";

const UserContext = createContext();

export const PERMISSION_OVERRIDE_KEY = "bkadmin_role_ui_overrides";

const CRM_CONTACT_LIST_URL =
  "https://crm-mqtt-718408119422.asia-east1.run.app/api/v1/contact/list?sortfield=t.rowid&sortorder=ASC&limit=1000";
const CRM_MDM_AUTH = "57b2a9ba-4428-48e4-b137-f8337b68595e";

const parseMerchantTags = (rawTags) => {
  if (Array.isArray(rawTags)) {
    return rawTags.map((tag) => String(tag).trim()).filter(Boolean);
  }
  if (typeof rawTags === "string") {
    return rawTags
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
  }
  return [];
};

const fetchMerchantScopeFromCRM = async (email) => {
  const normalizedEmail = String(email || "")
    .trim()
    .toLowerCase();
  if (!normalizedEmail) {
    return { tags: [], merchantId: "" };
  }

  const response = await fetch(CRM_CONTACT_LIST_URL, {
    method: "GET",
    headers: {
      "Mdm-Auth": CRM_MDM_AUTH,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch merchant scope from CRM API");
  }

  const crmData = await response.json();
  if (crmData.code !== 200 || !Array.isArray(crmData.data)) {
    throw new Error("Invalid merchant scope response from CRM API");
  }

  const userContact = crmData.data.find(
    (contact) =>
      String(contact?.email || "")
        .trim()
        .toLowerCase() === normalizedEmail
  );
  const rawOptionsMerchantTags = userContact?.array_options?.options_merchant_tags;
  const tags = parseMerchantTags(rawOptionsMerchantTags);

  return {
    tags,
    merchantId: tags.join(","),
  };
};

export function UserProvider({ children }) {
  const [userData, setUserData] = useState(() => {
    const stored = sessionStorage.getItem("userData");
    return stored ? JSON.parse(stored) : null;
  });
  const [licenses, setLicenses] = useState(() => {
    const stored = sessionStorage.getItem("licenses");
    return stored ? JSON.parse(stored) : [];
  });
  const [posData, setPosData] = useState(() => {
    const stored = sessionStorage.getItem("posData");
    return stored ? JSON.parse(stored) : [];
  });
  const [posPidArray, setPosPidArray] = useState(() => {
    const stored = sessionStorage.getItem("posPidArray");
    return stored ? JSON.parse(stored) : [];
  });
  const [idToken, setIdToken] = useState(() => {
    const stored = sessionStorage.getItem("idToken");
    return stored ? stored : null;
  });
  const [hierarchyData, setHierarchyData] = useState(() => {
    const stored = sessionStorage.getItem("hierarchyData");
    return stored ? JSON.parse(stored) : null;
  });
  const [isLoadingHierarchy, setIsLoadingHierarchy] = useState(false);
  const [rawRole, setRawRole] = useState(() => {
    return sessionStorage.getItem("userRawRole") || null;
  });
  const [role, setRole] = useState(() => {
    return sessionStorage.getItem("userRole") || null;
  });
  const [roleLevel, setRoleLevel] = useState(() => {
    const stored = sessionStorage.getItem("userRoleLevel");
    return normalizeRoleLevel(stored);
  });
  const [merchantTags, setMerchantTags] = useState(() => {
    const stored = sessionStorage.getItem("merchantTags");
    if (!stored) return [];
    try {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      console.warn("Failed to parse merchantTags from sessionStorage:", err);
      return [];
    }
  });
  const [merchantId, setMerchantId] = useState(() => {
    return sessionStorage.getItem("merchantId") || "";
  });

  // 當 userData 變更時，儲存到 sessionStorage
  useEffect(() => {
    if (userData !== null) {
      sessionStorage.setItem("userData", JSON.stringify(userData));
      console.log("Saved userData to sessionStorage:", userData);
    } else {
      sessionStorage.removeItem("userData");
      console.log("Removed userData from sessionStorage");
    }
  }, [userData]);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (user) {
        const token = await user.getIdToken(); // 自動刷新 token
        setIdToken(token);
        sessionStorage.setItem("idToken", token);
        // console.log("ID Token refreshed:", token);
      } else {
        setIdToken(null);
        sessionStorage.removeItem("idToken");
        console.log("User signed out, token removed");
      }
    });

    return () => unsubscribe();
  }, []);

  // 當 role 變更時，儲存到 sessionStorage
  useEffect(() => {
    if (role !== null) {
      sessionStorage.setItem("userRole", role);
      console.log("Saved role to sessionStorage:", role);
    } else {
      sessionStorage.removeItem("userRole");
      console.log("Removed role from sessionStorage");
    }
  }, [role]);

  useEffect(() => {
    if (roleLevel !== null && roleLevel !== undefined) {
      sessionStorage.setItem("userRoleLevel", String(roleLevel));
    } else {
      sessionStorage.removeItem("userRoleLevel");
    }
  }, [roleLevel]);

  useEffect(() => {
    if (rawRole !== null) {
      sessionStorage.setItem("userRawRole", rawRole);
      console.log("Saved rawRole to sessionStorage:", rawRole);
    } else {
      sessionStorage.removeItem("userRawRole");
      console.log("Removed rawRole from sessionStorage");
    }
  }, [rawRole]);

  useEffect(() => {
    sessionStorage.setItem("merchantTags", JSON.stringify(merchantTags || []));
  }, [merchantTags]);

  useEffect(() => {
    if (merchantId) {
      sessionStorage.setItem("merchantId", merchantId);
    } else {
      sessionStorage.removeItem("merchantId");
    }
  }, [merchantId]);

  // 從 CRM API 獲取 role : bkadmin
  const fetchUserRole = async (email) => {
    try {
      const merchantScope = await fetchMerchantScopeFromCRM(email);
      const response = await fetch(getGraphQLEndpoint(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: `
            query GetIamRoles($email: String!) {
              GetIamRoles(email: $email)
            }
          `,
          variables: { email },
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch data from IAM API");
      }

      const payload = await response.json();
      if (payload?.errors?.length) {
        throw new Error(payload.errors[0]?.message || "GraphQL error");
      }

      const roleData = payload?.data?.GetIamRoles?.data;
      const currentRole = Array.isArray(roleData?.role) ? roleData.role[0] : null;
      const numericRoleLevel = normalizeRoleLevel(currentRole?.role_level);

      if (numericRoleLevel) {
        const userRole = getRoleLabelByLevel(numericRoleLevel);
        console.log(`Role level fetched from IAM: ${numericRoleLevel}`);
        setRole(userRole);
        setRawRole(currentRole?.name || null);
        setRoleLevel(numericRoleLevel);
        setMerchantTags(merchantScope.tags);
        setMerchantId(merchantScope.merchantId);
        return numericRoleLevel;
      }

      console.warn("User role level not found in IAM roles.");
      setRole(null);
      setRawRole(null);
      setRoleLevel(null);
      setMerchantTags([]);
      setMerchantId("");
      return null;
    } catch (error) {
      console.error("Error fetching role:", error.message);
      setRole(null);
      setRawRole(null);
      setRoleLevel(null);
      setMerchantTags([]);
      setMerchantId("");
      return null;
    }
  };

  useEffect(() => {
    if (roleLevel === null || roleLevel === undefined) {
      if (role !== null) {
        setRole(null);
      }
      return;
    }
    const label = getRoleLabelByLevel(roleLevel) || null;
    if (role !== label) {
      setRole(label);
    }
  }, [role, roleLevel]);

  // 從 Firestore 獲取 userData
  const fetchUserData = async (email) => {
    try {
      const db = getFirestore();
      const userDocRef = doc(db, "users", email);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const data = userDocSnap.data();
        console.log("User data fetched from Firestore:", data);
        setUserData(data);
        return data;
      } else {
        console.warn("User data not found in Firestore for email:", email);
        setUserData(null);
        return null;
      }
    } catch (error) {
      console.error("Error fetching user data:", error.message);
      setUserData(null);
      return null;
    }
  };

  return (
    <UserContext.Provider
      value={{
        userData,
        setUserData,
        licenses,
        setLicenses,
        posData,
        setPosData,
        posPidArray,
        setPosPidArray,
        idToken,
        setIdToken,
        hierarchyData,
        setHierarchyData,
        isLoadingHierarchy,
        setIsLoadingHierarchy,
        rawRole,
        setRawRole,
        role,
        setRole,
        roleLevel,
        setRoleLevel,
        merchantTags,
        setMerchantTags,
        merchantId,
        setMerchantId,
        fetchUserRole,
        fetchUserData, // 新增 fetchUserData
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useUserContext() {
  return useContext(UserContext);
}
